import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { tenantApi } from '../../api/endpoints';
import type { Company, CompanySettings } from '../../api/types';
import { rememberCompany } from './start';

// The pure helpers (which company, which path) live in start.ts, unit-tested; re-exported here.
export { canOpen, dashboardPath, filesPath, lastCompany, rememberCompany, startCompany, startPath } from './start';

export function useCompanies() {
  return useQuery({ queryKey: ['companies'], queryFn: tenantApi.companies, staleTime: 60_000 });
}

/** The company in the address (/c/:tenantId/…), and whether the caller belongs to it or is a guest. */
export function useCurrentCompany(): { tenantId: string | null; company: Company | null; isMember: boolean; isAdmin: boolean; loading: boolean } {
  const { tenantId = null } = useParams();
  const companies = useCompanies();
  const company = companies.data?.find((candidate) => candidate.id === tenantId) ?? null;
  useEffect(() => {
    if (company) rememberCompany(company.id);
  }, [company]);
  const role = company?.role;
  return {
    tenantId,
    company,
    isMember: role === 'owner' || role === 'admin' || role === 'member',
    isAdmin: role === 'owner' || role === 'admin',
    loading: companies.isLoading,
  };
}

/** Somebody from outside the company in its teams (TF-302): their teams' records only, nothing of their own to make. */
export function useOutsider(): boolean {
  return useCurrentCompany().company?.role === 'external';
}

const DEFAULT_SETTINGS: CompanySettings = { mapProvider: 'google', newRecordsPrivate: false, appearance: null , supportContact: null };

/**
 * Whether a tool of the Marketplace is switched on in the current company (TF-171): its screens and
 * buttons show only then. While the company list loads - or from a service older than the Marketplace -
 * everything counts as on, so nothing flickers away.
 */
export function useTool(tool: string): boolean {
  const { company } = useCurrentCompany();
  return company?.tools === undefined || company.tools.includes(tool);
}

/** The current company's settings (TF-40), the defaults while they load. */
export function useCompanySettings(): CompanySettings {
  const { company } = useCurrentCompany();
  return { ...DEFAULT_SETTINGS, ...company?.settings };
}

/**
 * A company just registered (TF-79): in the list at once - the answer is the whole company, with the
 * owner's role - and the list asked again behind it, not waited for: it may be long for somebody in
 * many companies, and the new one opens now. A first load of the list still on its way is called off
 * first: it was asked before the company existed, and asking again would only have waited for it.
 */
export async function addCreatedCompany(queryClient: QueryClient, created: Company): Promise<void> {
  await queryClient.cancelQueries({ queryKey: ['companies'] });
  queryClient.setQueryData<Company[]>(['companies'], (companies) => (companies?.some((one) => one.id === created.id) ? companies : [...(companies ?? []), created]));
  void queryClient.invalidateQueries({ queryKey: ['companies'] });
  rememberCompany(created.id);
}
